<style scoped lang="scss">
.report-card {
	cursor: pointer;
	transition: background-color 0.2s;
}

.report-card:hover {
	background-color: $secondary;
}

.report-card.active {
	background-color: $primary;
	color: white;
}
.reports-list {
	overflow-y: auto;
	max-height: 100vh;
}
</style>

<template>
	<!-- Communication card: icons, reporter info, address badge, time -->
	<div
		class="align-items-center list-group-item p-3 report-card"
		:class="{ active: isSelected }"
	>
		<div class="d-flex align-items-start justify-content-between">
			<div class="d-flex align-items-center gap-2">
				<Tooltip placement="top" :title="tooltipTitleForCommunicationType()">
					<i class="bi fs-4" :class="iconForCommunicationType()"></i>
				</Tooltip>
				<Tooltip
					v-if="iconForReportType()"
					placement="top"
					:title="tooltipTitleForReportType()"
				>
					<i class="bi fs-4" :class="iconForReportType()"></i>
				</Tooltip>
				<!-- Phone intake badge -->
				<Tooltip
					v-if="comm.is_phone_intake"
					placement="top"
					title="Phone intake report"
				>
					<span class="badge bg-primary-subtle text-primary-emphasis small">
						<i class="bi bi-telephone-fill me-1"></i>Phone
					</span>
				</Tooltip>
			</div>
			<small>
				<TimeRelativeWithTooltip :time="comm.created" />
			</small>
		</div>
		<!-- Reporter name and address. The status badge is the row's last word:
		     it sits at the bottom right, on the sender's own line, so it reads as
		     a property of the exchange rather than of the channel icons above.
		     The line renders for the badge alone too, so a finished report from an
		     unnamed reporter still states how it finished. -->
		<div
			v-if="comm.address_raw || comm.contact.name || statusBadge"
			class="mt-1 small"
		>
			<div
				v-if="comm.address_raw"
				class="text-truncate"
				:title="comm.address_raw"
			>
				<i class="bi bi-geo-alt me-1 text-muted"></i>
				{{ comm.address_raw }}
			</div>
			<div
				v-if="comm.contact.name || statusBadge"
				class="d-flex align-items-center mt-1 text-muted"
			>
				<span v-if="comm.contact.name" class="me-2">
					<i class="bi bi-person me-1"></i>{{ comm.contact.name }}
				</span>
				<span
					v-if="statusBadge"
					class="badge rounded-pill ms-auto"
					:class="`text-bg-${statusBadge.variant}`"
				>
					<i class="bi me-1" :class="statusBadge.icon"></i
					>{{ statusBadge.label }}
				</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import Tooltip from "@/components/Tooltip.vue";
import { communicationStatusBadge } from "@/components/communicationStatus";
import { Communication } from "@/type/api";
interface Props {
	comm: Communication;
	isSelected: boolean;
}
const props = defineProps<Props>();
// Finished work carries the badge that says how it finished; open work carries
// none, so the queue reads exactly as it did before there were badges.
const statusBadge = computed(() => communicationStatusBadge(props.comm.status));
function iconForCommunicationType(): string {
	switch (props.comm.type) {
		case "email":
			return "bi-envelope";
		case "text":
			return "bi-chat-dots";
		case "phone_call":
			return "bi-telephone";
		case "paper_letter":
			return "bi-envelope-paper";
		case "in_person":
			return "bi-person-walking";
		case "public_report":
			return "bi-postcard";
		case "virtual_inspection":
			return "bi-camera-video";
		default:
			return "bi-question-circle";
	}
}
// The kind of report a report communication stands for is the related record's
// type — "publicreport.compliance", "publicreport.nuisance" or
// "publicreport.water" — because the communication's own type says only that it
// came from a report. A communication relating to no report has none.
function relatedReportType(): string | null {
	const report = props.comm.related.find((r) =>
		r.type.startsWith("publicreport."),
	);
	return report ? report.type : null;
}
function iconForReportType(): string {
	switch (relatedReportType()) {
		case "publicreport.compliance":
			return "bi-card-checklist";
		case "publicreport.nuisance":
			return "bi-mosquito";
		case "publicreport.water":
			return "bi-droplet-fill";
		default:
			return "";
	}
}
function tooltipTitleForCommunicationType(): string {
	switch (props.comm.type) {
		case "email":
			return "An email received from a member of the public";
		case "text":
			return "An SMS/MMS text message received from a member of the public";
		case "phone_call":
			return "A phone call received from a resident";
		case "paper_letter":
			return "A personal letter received from a resident through the mail";
		case "in_person":
			return "An exchange with a resident in person";
		case "public_report":
			return "A report made by a member of the public to the district";
		case "virtual_inspection":
			return "A Mosquito Check: a virtual inspection requested by a resident";
		default:
			return "The channel this exchange came through was not recorded";
	}
}
function tooltipTitleForReportType(): string {
	switch (relatedReportType()) {
		case "publicreport.compliance":
			return "A compliance report either made by scanning a door hanger or by receiving a personal letter through the mail";
		case "publicreport.nuisance":
			return "A report of a mosquito nuisance";
		case "publicreport.water":
			return "A report of standing water";
		default:
			return "";
	}
}
</script>
